// src/data/manualContent.ts
//
// Contenido del Manual de Usuario, organizado por módulo.
// La clave "permissionKey" DEBE coincidir con la key usada en
// ModulePermissions / usePermissions().canView(key) (ver Permissions.tsx
// y RoleManagement.tsx) para que el manual se filtre automáticamente
// según lo que cada rol puede ver.
//
// Para agregar un módulo nuevo al sistema: agrega aquí una entrada con su
// permissionKey y el manual se actualiza solo para los roles que tengan
// acceso a ese módulo. No hay que tocar nada más.

export interface ManualSection {
  id: string;
  permissionKey: string; // debe existir en ModulePermissions
  group: 'Principal' | 'Clínica' | 'CRM' | 'Sistema';
  title: string;
  summary: string;
  steps?: string[];
  tips?: string[];
  notes?: string[];
}

export const manualContent: ManualSection[] = [
{
  id: 'dashboard',
  permissionKey: 'dashboard',
  group: 'Principal',
  title: 'Panel de Control',
  summary: 'Es la pantalla de inicio: te da un vistazo rápido del estado del negocio (ingresos, citas y productos más vendidos) sin tener que entrar a cada módulo por separado.',
  steps: [
  'Al iniciar sesión, el Panel de Control se muestra automáticamente como pantalla principal.',
  'Revisa las tarjetas de resumen: ingresos del período, citas agendadas y productos más vendidos.',
  'Cambia el rango de fechas si quieres ver otro período (por ejemplo, la semana o el mes anterior).',
  'Usa los botones "Exportar a Excel" o "Exportar a PDF" para descargar el reporte cuando lo necesites compartir o imprimir.'],

  tips: ['Revisa el panel cada mañana antes de empezar; te ayuda a priorizar el trabajo del día con base en lo que realmente está pasando en la clínica.'],
  notes: ['Lo que ves aquí depende de tu rol: algunos usuarios solo ven sus propias métricas (por ejemplo, un doctor ve solo sus citas) y no las de toda la clínica.']
},
{
  id: 'patients',
  permissionKey: 'patients',
  group: 'Principal',
  title: 'Pacientes',
  summary: 'Aquí vive la ficha de cada paciente: sus datos personales, cómo contactarlo y un acceso rápido a su historial dentro del sistema.',
  steps: [
  'Usa la barra de búsqueda para encontrar un paciente por nombre, cédula, correo o teléfono; no hace falta escribir el dato completo.',
  'Para registrar uno nuevo, presiona "Agregar" y completa nombre, dirección, teléfono, correo y contacto de emergencia.',
  'Desde la lista puedes Ver (consultar su ficha), Editar (corregir o actualizar datos), Archivar (sacarlo de la vista activa sin borrarlo), Desactivar o Restaurar según el caso.'],

  tips: ['Antes de crear un paciente nuevo, búscalo primero por teléfono o cédula: así evitas duplicados si ya estaba registrado.'],
  notes: ['Archivar o desactivar un paciente NO borra su historial ni sus citas anteriores; solo lo oculta de la vista principal para mantenerla ordenada.']
},
{
  id: 'patient360',
  permissionKey: 'patient360',
  group: 'Principal',
  title: 'Cliente 360',
  summary: 'Es la "vista completa" de un paciente: en una sola pantalla ves sus citas, compras, tratamientos y notas, sin tener que ir módulo por módulo a buscar la información.',
  steps: [
  'Busca al paciente que necesitas consultar.',
  'Revisa de un vistazo su historial de citas, compras, tratamientos activos y notas de seguimiento.'],

  tips: ['Ábrelo justo antes de atender o llamar a un paciente: te da todo el contexto necesario en segundos, sin sorpresas durante la conversación.']
},
{
  id: 'session-packages',
  permissionKey: 'sessionPackages',
  group: 'Principal',
  title: 'Paquetes de Sesiones',
  summary: 'Sirve para controlar tratamientos que se pagan y consumen por sesiones (por ejemplo, un paquete de 6 sesiones de fisioterapia), llevando la cuenta de cuántas quedan disponibles.',
  steps: [
  'Crea el paquete indicando el número total de sesiones y asígnalo al paciente correspondiente.',
  'Cada vez que el paciente use una sesión, regístrala en el sistema para descontarla del total.',
  'Consulta en cualquier momento cuántas sesiones le quedan al paciente antes de agendar otra cita del mismo paquete.'],

  tips: ['Revisa el saldo de sesiones antes de agendar una nueva cita del paquete, para evitar cobrar de más o de menos.']
},
{
  id: 'appointments',
  permissionKey: 'appointments',
  group: 'Principal',
  title: 'Citas',
  summary: 'Aquí administras toda la agenda de la clínica: crea nuevas citas, edítalas si algo cambia, y dales seguimiento hasta que el paciente sea atendido.',
  steps: [
  'Presiona el botón "Agregar" (arriba a la derecha) para abrir el formulario de nueva cita.',
  'Elige al paciente (o créalo si es nuevo), luego selecciona el doctor y el o los servicios que se van a realizar.',
  'Define la fecha y hora de la cita. Si necesitas dejar alguna aclaración para el equipo, usa el campo de notas.',
  'Guarda la cita: quedará visible en la agenda con estado "Pendiente".',
  'Cuando el paciente llegue o la cita se realice, actualiza su estado (por ejemplo a "Completada", "Cancelada" o "No asistió") para mantener la agenda al día.',
  'Usa los filtros de doctor y estado en la parte superior de la lista para encontrar citas específicas sin tener que revisarlas todas.'],

  tips: [
  'Revisa la agenda del día cada mañana para confirmar qué citas siguen pendientes y así evitar imprevistos.',
  'Si editas una cita ya guardada, asegúrate de guardar los cambios antes de salir del formulario; de lo contrario se perderán.'],

  notes: ['Si tu rol es de solo lectura, podrás ver la agenda pero no crear, editar ni cambiar el estado de las citas.']
},
{
  id: 'clinical-history',
  permissionKey: 'clinicalHistory',
  group: 'Principal',
  title: 'Historial Clínico',
  summary: 'Guarda el registro médico de cada paciente: diagnósticos, tratamientos aplicados y cómo ha evolucionado a lo largo del tiempo.',
  steps: [
  'Busca al paciente y abre su historial para ver todas sus visitas anteriores en orden cronológico.',
  'Al finalizar una consulta, agrega una nueva entrada con el diagnóstico, tratamiento indicado y observaciones.',
  'Consulta entradas anteriores antes de una nueva visita para dar seguimiento consistente al caso del paciente.'],

  notes: ['Esta información es confidencial. Solo debe editarla el personal clínico autorizado; el resto del equipo puede tener acceso limitado o nulo.']
},
{
  id: 'goals',
  permissionKey: 'goals',
  group: 'Principal',
  title: 'Metas y Alertas',
  summary: 'Te permite fijar objetivos de citas y ventas (diarios o mensuales) y ver en tiempo real qué tan cerca está el equipo de cumplirlos.',
  steps: [
  'Define la meta (por ejemplo, número de citas o monto de ventas) y el período al que aplica.',
  'Revisa el avance mostrado en las barras o indicadores del módulo.',
  'Ajusta la meta si las condiciones del negocio cambian (temporada alta o baja, nuevo servicio, etc.).'],

  tips: ['Compara el avance de la meta a mitad del período para saber si hace falta un empujón extra antes de que termine.']
},
{
  id: 'doctors',
  permissionKey: 'doctors',
  group: 'Clínica',
  title: 'Doctores',
  summary: 'Aquí se registra al personal médico: sus datos, especialidad y disponibilidad. Esta información es la que aparece luego al agendar una cita.',
  steps: [
  'Registra al doctor con su nombre, especialidad y datos de contacto.',
  'Define su disponibilidad u horario si el sistema lo permite, para que la agenda de Citas la respete.',
  'Mantén sus datos actualizados: cualquier cambio se refleja automáticamente al crear nuevas citas.']

},
{
  id: 'services',
  permissionKey: 'services',
  group: 'Clínica',
  title: 'Servicios',
  summary: 'Es el catálogo de tratamientos o servicios que ofrece la clínica, con su precio y duración. Se usa directamente al crear una cita.',
  steps: [
  'Agrega cada servicio con su nombre, precio y duración estimada.',
  'Agrúpalos por especialidad si aplica, para que sean más fáciles de encontrar al agendar.',
  'Actualiza precios o duraciones aquí cuando cambien, y se reflejará en toda la app.']

},
{
  id: 'offers',
  permissionKey: 'offers',
  group: 'Clínica',
  title: 'Ofertas',
  summary: 'Sirve para crear promociones y descuentos por tiempo limitado sobre servicios o productos, y controlar cuándo empiezan y terminan.',
  steps: [
  'Crea la oferta indicando a qué servicio o producto aplica y el descuento correspondiente.',
  'Define la fecha de inicio y fin para que se active y desactive automáticamente.',
  'Revisa periódicamente las ofertas activas para asegurarte de que sigan vigentes y sean correctas.']

},
{
  id: 'gift-certificates',
  permissionKey: 'giftCertificates',
  group: 'Clínica',
  title: 'Certificados de Regalo',
  summary: 'Controla la emisión, el saldo y el uso (redención) de certificados o vales de regalo que los pacientes pueden comprar o recibir.',
  steps: [
  'Emite el certificado indicando el monto y, si aplica, a quién va dirigido.',
  'Cuando el paciente lo use, regístralo como redimido para descontar el saldo correspondiente.',
  'Consulta el saldo disponible de un certificado antes de aplicarlo a una compra o servicio.']

},
{
  id: 'loyalty',
  permissionKey: 'loyalty',
  group: 'Clínica',
  title: 'Tarjetas de Fidelidad',
  summary: 'Es el programa de lealtad para pacientes frecuentes: acumulan visitas o beneficios que luego pueden canjear.',
  steps: [
  'Inscribe al paciente en el programa de fidelidad.',
  'Registra cada visita o compra que sume puntos o avances según las reglas del programa.',
  'Consulta su progreso para saber cuándo puede canjear un beneficio.']

},
{
  id: 'products',
  permissionKey: 'products',
  group: 'Clínica',
  title: 'Productos',
  summary: 'Catálogo de productos físicos que vende la clínica, junto con su inventario disponible y precio.',
  steps: [
  'Registra el producto con nombre, precio y cantidad en inventario.',
  'Actualiza el inventario cada vez que entren o salgan unidades (compra a proveedor o venta a paciente).',
  'Revisa el stock antes de prometerle un producto a un paciente, para evitar quedar sin existencias.']

},
{
  id: 'insurance',
  permissionKey: 'insurance',
  group: 'Clínica',
  title: 'Seguros',
  summary: 'Registra las aseguradoras con las que trabaja la clínica y cómo se relacionan con los pacientes que tienen cobertura.',
  steps: [
  'Registra la aseguradora con sus datos de contacto.',
  'Vincula a los pacientes que tengan cobertura con la aseguradora correspondiente.',
  'Consulta esta información cuando necesites confirmar la cobertura de un paciente.']

},
{
  id: 'specialties',
  permissionKey: 'specialties',
  group: 'Clínica',
  title: 'Especialidades',
  summary: 'Catálogo de especialidades médicas o estéticas de la clínica, usado para clasificar tanto a los doctores como a los servicios que ofrecen.',
  steps: [
  'Crea cada especialidad que maneje la clínica (por ejemplo, dermatología, fisioterapia).',
  'Asigna la especialidad correspondiente a cada doctor y a cada servicio del catálogo.',
  'Usa este catálogo para filtrar doctores o servicios por especialidad en otros módulos.']

},
{
  id: 'contacts',
  permissionKey: 'contacts',
  group: 'CRM',
  title: 'Contactos',
  summary: 'Es tu base de datos comercial: prospectos, proveedores y aliados, sean o no pacientes de la clínica.',
  steps: [
  'Busca contactos existentes con la barra de búsqueda antes de crear uno nuevo.',
  'Crea uno nuevo con nombre, teléfono, correo y notas relevantes sobre la relación.',
  'Exporta el listado a Excel o PDF cuando necesites compartirlo o analizarlo fuera del sistema.']

},
{
  id: 'companies',
  permissionKey: 'companies',
  group: 'CRM',
  title: 'Empresas',
  summary: 'Registro de empresas relacionadas con contactos u oportunidades comerciales (por ejemplo, un convenio corporativo).',
  steps: [
  'Registra la empresa con su nombre y datos de contacto principales.',
  'Vincúlala a los contactos u oportunidades que correspondan.',
  'Consúltala para tener contexto completo cuando negocies con alguien de esa empresa.']

},
{
  id: 'opportunities',
  permissionKey: 'opportunities',
  group: 'CRM',
  title: 'Oportunidades',
  summary: 'Representa negocios potenciales en curso, como un paquete de tratamientos que un paciente todavía está evaluando comprar.',
  steps: [
  'Crea la oportunidad con un nombre descriptivo que identifique claramente el negocio.',
  'Filtra por etapa para ver en qué punto del proceso de venta se encuentra cada una.',
  'Agrega notas de la negociación y actualiza su estado conforme avanza, hasta cerrarla como ganada o perdida.'],

  tips: ['Actualiza el estado de la oportunidad justo después de cada conversación con el cliente; así el equipo siempre ve información al día.']
},
{
  id: 'follow-up-notes',
  permissionKey: 'followUpNotes',
  group: 'CRM',
  title: 'Notas de Seguimiento',
  summary: 'Es el registro cronológico de cada interacción (llamadas, mensajes, visitas) con un contacto o paciente, para no perder el hilo de la conversación.',
  steps: [
  'Agrega una nota cada vez que tengas una interacción relevante con el contacto o paciente.',
  'Incluye la fecha, el medio usado (llamada, WhatsApp, visita) y un resumen breve de lo hablado.',
  'Revisa las notas anteriores antes de contactar de nuevo a la persona, para continuar la conversación con contexto.']

},
{
  id: 'commissions',
  permissionKey: 'commissions',
  group: 'CRM',
  title: 'Comisiones',
  summary: 'Calcula automáticamente las comisiones del equipo según las ventas o servicios que realizaron.',
  steps: [
  'Verifica que las ventas y servicios estén correctamente registrados en sus módulos correspondientes, ya que de ahí se calculan las comisiones.',
  'Consulta el reporte de comisiones por el período que necesites (por ejemplo, quincenal o mensual).',
  'Exporta el reporte si necesitas entregarlo para el pago de nómina.']

},
{
  id: 'intelligence',
  permissionKey: 'intelligence',
  group: 'CRM',
  title: 'Inteligencia',
  summary: 'Ofrece indicadores y análisis avanzados sobre tendencias del negocio y comportamiento de los pacientes, más allá de lo que muestra el Panel de Control.',
  steps: [
  'Selecciona el indicador o análisis que te interese revisar.',
  'Ajusta el período de tiempo para comparar tendencias entre distintas fechas.',
  'Usa estos datos para tomar decisiones, como ajustar precios, promociones o el enfoque comercial.']

},
{
  id: 'bot-omnichannel',
  permissionKey: 'botOmnichannel',
  group: 'CRM',
  title: 'Bot Omnichannel',
  summary: 'Configura el asistente automático que responde consultas de pacientes y prospectos por WhatsApp, redes sociales y correo.',
  steps: [
  'Revisa y ajusta las respuestas automáticas del bot según los servicios y horarios actuales de la clínica.',
  'Supervisa las conversaciones que el bot no pudo resolver, para que un miembro del equipo las atienda.',
  'Actualiza la información del bot (precios, promociones, horarios) cada vez que cambie en la clínica.'],

  tips: ['Revisa periódicamente las conversaciones marcadas como pendientes: son consultas reales que el bot no supo responder y que un humano debe seguir.']
},
{
  id: 'audit-log',
  permissionKey: 'auditLog',
  group: 'Sistema',
  title: 'Bitácora',
  summary: 'Es el historial de auditoría del sistema: registra quién creó, editó o eliminó cada dato y en qué momento, útil para revisar cambios o resolver dudas.',
  steps: [
  'Filtra por usuario, módulo o fecha para encontrar un cambio específico.',
  'Revisa el detalle de cada registro para ver exactamente qué se modificó.',
  'Usa la bitácora para aclarar dudas sobre cambios inesperados en algún registro.']

},
{
  id: 'settings',
  permissionKey: 'settings',
  group: 'Sistema',
  title: 'Configuración',
  summary: 'Es el panel de administración general del sistema: roles y permisos, respaldos de información, notificaciones por correo e integración con Google Sheets.',
  steps: [
  'Entra a "Roles y Permisos" para definir qué puede ver y hacer cada tipo de usuario.',
  'Configura los respaldos automáticos para proteger la información de la clínica.',
  'Ajusta las notificaciones por correo y la integración con Google Sheets según las necesidades del negocio.'],

  notes: ['El acceso a "Roles y Permisos" debe limitarse a administradores, ya que desde ahí se controla lo que el resto del equipo puede hacer en todo el sistema.']
}];